/**
 * @file index.tsx
 *
 * @fileoverview Renders Bookmark component, removes and deletes job bookmark.
 * This component can be used inside of BookmarkedJobsContext and independently of it.
 * If we want a component to update the context we must set reloadAfterMutation to
 * true, otherwise, the component reloads the page to update job bookmark data.
 */
import REMOVE_JOB_BOOKMARK from '~/jobiqo/components/BookmarkedJobs/graphql/mutations/removeJobBookmark.gql';
import ADD_JOB_BOOKMARK from '~/jobiqo/components/BookmarkedJobs/graphql/mutations/addJobBookmark.gql';
import { gql, useMutation } from '@apollo/client';
import React, {
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { Job } from '~/jobiqo/types/types';
import { useFlags } from '~/jobiqo/hooks/flags';
import { FormattedMessage } from 'react-intl';
import Router from 'next/router';
import {
  BookmarkedJobsContext,
  BookmarkedJobsContextInterface,
} from '~/jobiqo/components/BookmarkedJobs/context';
import { analyticsDispatch, EVENTS } from '~/jobiqo/lib/analytics';
import { getJobBookmarkDimensions } from '~/jobiqo/components/BookmarkedJobs/utils/job_bookmark_analytics';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import LoginModal from '~/jobiqo/components/User/Login/Modal';
import { isRecruiter } from '~/jobiqo/lib/auth';
import JobBookmarkPlacement from '~/jobiqo/components/BookmarkedJobs/enums/JobBookmarkPlacement';
import { Bookmark } from '~/vcl/atoms/28-bookmark';
import { Alert } from '~/vcl/atoms/18-alert';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

export interface BookmarkedJobsBookmarkProps {
  /**
   * The bookmark placement.
   *
   * @default JobBookmarkPlacement.JOB_TEASER
   */
  placement?: JobBookmarkPlacement;
  /**
   * The bookmarked job.
   */
  job: Job;
  /**
   * The remove/add bookmark mutation callback.
   */
  bookmarkMutationCallback?: (isBookmarked?: boolean) => void;
  /**
   * The flag to reload after mutation.
   *
   * @default false
   */
  reloadAfterMutation?: boolean;
}

export const BookmarkedJobsBookmark = ({
  placement = JobBookmarkPlacement.JOB_TEASER,
  job,
  reloadAfterMutation = false,
  bookmarkMutationCallback,
}: BookmarkedJobsBookmarkProps) => {
  const { JOBS } = useFrontendConfig();
  const { user }: UserContextInterface = useContext(UserContext);

  const intl = useIntl(),
    flagService = useFlags(),
    {
      bookmarkedJobs,
      setBookmarkedJobs,
      getBookmarkedJob,
    }: BookmarkedJobsContextInterface = useContext(BookmarkedJobsContext);
  const memoizedBookmarkedJobs = React.useMemo(() => bookmarkedJobs, [
    bookmarkedJobs,
  ]);

  const [isBookmarked, setIsBookmarked] = useState<boolean>(
      Boolean(job.bookmarked),
    ),
    [showRemoveConfirm, setShowRemoveConfirm] = useState<boolean>(false),
    [showLoginModal, setShowLoginModal] = useState<boolean>(false);

  const [updateBookmark] = useMutation(
    isBookmarked ? REMOVE_JOB_BOOKMARK : ADD_JOB_BOOKMARK,
    {
      variables: {
        nid: job.id,
      },
      onCompleted({ bookmark }) {
        setShowRemoveConfirm(false);
        bookmarkMutationCallback?.(!isBookmarked);

        if (reloadAfterMutation) {
          Router.reload();
        }
        // If we want don't want to reload, we are relying
        // on the bookmarked context, so we should update it accordingly.
        else {
          setBookmarkedJobs(
            isBookmarked
              ? // Remove the job.
                memoizedBookmarkedJobs.filter(
                  (bookmarkedJob) => bookmarkedJob.id !== job.id,
                )
              : // Add a new bookmarked job.
                [...memoizedBookmarkedJobs, job],
          );
        }

        if (bookmark.errors.length > 0) {
          flagService.addFlag({
            type: 'danger',
            text: isBookmarked
              ? intl.formatMessage({
                  id: 'bookmarked_jobs_delete_error',
                  defaultMessage:
                    'An error happened while deleting the job bookmark',
                })
              : intl.formatMessage({
                  id: 'bookmarked_jobs_remove_error',
                  defaultMessage:
                    'An error happened while creating the job bookmark',
                }),
          });
        } else {
          flagService.addFlag({
            type: 'success',
            text: isBookmarked
              ? intl.formatMessage({
                  id: 'bookmarked_jobs_remove_success',
                  defaultMessage: 'Job bookmark removed successfully',
                })
              : intl.formatMessage({
                  id: 'bookmarked_jobs_add_success',
                  defaultMessage: 'Job bookmark added successfully',
                }),
          });
        }

        // Dispatch Google analytics event.
        if (!isBookmarked) {
          analyticsDispatch(
            EVENTS.JOB_BOOKMARK,
            getJobBookmarkDimensions(job, user, placement),
          );
        }
      },
      update(cache) {
        cache.evict({ id: `Job:${job.id}` });
        // Update cached job bookmarked field.
        cache.writeFragment({
          data: { ...job, bookmarked: !job.bookmarked },
          fragment: gql`
            fragment UpdateJobBookmark on Job {
              bookmarked
            }
          `,
        });
      },
    },
  );

  useEffect(() => {
    const isJobBookmarked = getBookmarkedJob && !!getBookmarkedJob(job.id);
    if (isJobBookmarked !== isBookmarked) {
      setIsBookmarked(!!isJobBookmarked);
    }
  }, [bookmarkedJobs, job, getBookmarkedJob]);

  const handleUpdateBookmark = useCallback(() => {
    if (user) {
      isBookmarked ? setShowRemoveConfirm(true) : updateBookmark();
    } else {
      startTransition(() => setShowLoginModal(true));
    }
  }, [
    user,
    isBookmarked,
    updateBookmark,
    setShowRemoveConfirm,
    setShowLoginModal,
  ]);

  if (job.type === 'job_per_backfill' || !JOBS.BOOKMARKS_ENABLED) {
    // We don't support bookmarking job_per_backfill jobs or when the flag is set to false.
    return null;
  }
  // Hide bookmark from the recruiter
  if (user && isRecruiter(user)) {
    return null;
  }

  return (
    <>
      <Bookmark
        variant={
          placement === JobBookmarkPlacement.JOB_DETAIL ? 'badge' : 'icon'
        }
        isBookmarked={isBookmarked}
        onBookmark={handleUpdateBookmark}
        text={
          isBookmarked
            ? intl.formatMessage({
                id: 'bookmarked_jobs_bookmarked',
                defaultMessage: 'bookmarked',
              })
            : intl.formatMessage({
                id: 'bookmarked_jobs_bookmark',
                defaultMessage: 'bookmark',
              })
        }
      />
      <Alert
        isShown={showRemoveConfirm}
        onConfirm={updateBookmark}
        onClose={() => setShowRemoveConfirm(false)}
        confirmText={
          <FormattedMessage
            id="bookmark_jobs_confirm_remove"
            defaultMessage="Yes, remove"
          />
        }
        cancelText={<FormattedMessage id="cancel" defaultMessage="Cancel" />}
        alertLabel={
          <FormattedMessage
            id="bookmark_jobs_confirm_remove_label"
            defaultMessage="remove"
          />
        }
      >
        <FormattedMessage
          id="bookmark_jobs_confirm_remove_text"
          defaultMessage="Are you sure you want to remove job bookmark?"
        />
      </Alert>
      <LoginModal
        onHide={() => setShowLoginModal(false)}
        isShown={showLoginModal}
        onLoginSuccess={() => window.location.reload()}
      />
    </>
  );
};

export default React.memo(BookmarkedJobsBookmark);
